import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import quotaService from '../../services/quotaService';

const PurchaseCreditsDisplay = ({ userId, compact = false }) => {
  const { purchaseCredits, loadUserPurchaseCredits } = useSubscription();

  // Load purchase credits data when component mounts and when userId changes
  React.useEffect(() => {
    if (userId) {
      loadUserPurchaseCredits();
    }
  }, [userId, loadUserPurchaseCredits]);

  // Set up periodic refresh for real-time updates
  React.useEffect(() => {
    if (!userId) return;

    // Refresh purchase credits every 30 seconds for real-time updates
    const intervalId = setInterval(() => {
      loadUserPurchaseCredits();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [userId, loadUserPurchaseCredits]);

  if (purchaseCredits?.loading) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-2 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  const limitCredits = purchaseCredits?.limit ?? 0;
  const remainingCredits = purchaseCredits?.remaining ?? 0;
  const usedCredits = Math.max(0, limitCredits - remainingCredits);

  if (!limitCredits || limitCredits === 0) {
    // Don't show anything if user has no purchase credits
    return null;
  }
  
  // Calculate usage percentage
  const usagePercentage = quotaService.calculateCreditUsagePercentage(usedCredits, limitCredits);
  const creditStatus = quotaService.getCreditStatus(usedCredits, limitCredits);
  const isOverLimit = quotaService.isOverCreditLimit(usedCredits, limitCredits);

  // Get status colors
  const getStatusColors = () => {
    switch (creditStatus) {
      case 'critical':
        return {
          bg: 'bg-red-100',
          text: 'text-red-800',
          progress: 'bg-red-600',
          border: 'border-red-200'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          progress: 'bg-yellow-600',
          border: 'border-yellow-200'
        };
      case 'safe':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          progress: 'bg-green-600',
          border: 'border-green-200'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          progress: 'bg-gray-600',
          border: 'border-gray-200'
        };
    }
  };

  const colors = getStatusColors();

  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0 min';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (compact) {
    return (
      <div className={`p-2 ${colors.bg} rounded-lg ${colors.border} border`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xs font-semibold text-gray-800">Free Credits:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors.text}`}>
              {creditStatus.toUpperCase()}
            </span>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold text-gray-800">
              {formatTime(remainingCredits)} / {formatTime(limitCredits)}
            </div>
            <div className="text-xs text-gray-600">{usagePercentage}% used</div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
          <div 
            className={`h-1 rounded-full ${colors.progress} transition-all duration-300`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 ${colors.bg} rounded-lg ${colors.border} border`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <h3 className="text-sm font-semibold text-gray-800">Monthly Free Purchase Credits</h3>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors.text}`}>
            {creditStatus.toUpperCase()}
          </span>
          <button
            onClick={() => loadUserPurchaseCredits()}
            disabled={purchaseCredits?.loading}
            className="ml-2 p-1 text-gray-500 hover:text-gray-700 transition-colors"
            title="Refresh credits data"
          >
            <svg className={`w-4 h-4 ${purchaseCredits?.loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-gray-800">
            {formatTime(remainingCredits)} / {formatTime(limitCredits)}
          </div>
          <div className="text-xs text-gray-600">{usagePercentage}% used</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div 
          className={`h-2 rounded-full ${colors.progress} transition-all duration-300`}
          style={{ width: `${Math.min(usagePercentage, 100)}%` }}
        ></div>
      </div>

      {/* Detailed Info */}
      <div className="grid grid-cols-3 gap-4 text-xs text-gray-600">
        <div className="text-center">
          <div className="font-semibold text-gray-800">{formatTime(limitCredits)}</div>
          <div>Total Credits</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-gray-800">{formatTime(usedCredits)}</div>
          <div>Used</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-gray-800">{formatTime(remainingCredits)}</div>
          <div>Remaining</div>
        </div>
      </div>

      {/* Over Limit Warning */}
      {isOverLimit && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-center mt-3">
          <div className="flex items-center justify-center mb-2">
            <svg className="w-4 h-4 text-red-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800 text-xs font-medium">
              Credit limit exceeded. Additional purchases will be charged.
            </span>
          </div>
        </div>
      )}

      {/* Low Credits Warning */}
      {!isOverLimit && usagePercentage >= 75 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-center mt-3">
          <div className="flex items-center justify-center mb-2">
            <svg className="w-4 h-4 text-yellow-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-yellow-800 text-xs font-medium">
              Running low on free credits. {formatTime(remainingCredits)} remaining.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseCreditsDisplay;
